import React, { useState, createContext, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { UserContext } from './User';
import { SettingsContext } from './Settings';

export const FeatureGatingContext = createContext();

export const plans = {
  FREEMIUM: 'freemium',
  CLUB: 'club',
  BLACK: 'black',
};

export const features = {
  ORDER_REQUESTS: 'order_requests',
  SELL_REQUESTS: 'sell_requests',
  MARKETPLACE_ACCESS: 'marketplace_access',
  PART_EXCHANGE: 'part_exchange',
  CLIENT_PRODUCT_REQUEST: 'client_product_request',
  LISTINGS: 'listings',
  SHARE_WHATSAPP: 'share_whatsapp',
  ADD_PRODUCT: 'add_product',
};

export const featureTypes = {
  AMOUNT: 'amount',
  BOOLEAN: 'boolean',
};

FeatureGatingContext.features = features;

const amountFeatures = [
  features.ORDER_REQUESTS,
  features.SELL_REQUESTS,
  features.LISTINGS,
];

const loadFeatureQuota = async (feature) => {
  try {
    const response = await axios.post(`/api/feature-available-quota`, { feature });

    return response.data.quota;
  } catch (error) {
    console.error('Error loading feature quota', error);

    return 0;
  }
}

export function FeatureGatingProvider(props) {
  const { user } = useContext(UserContext);
  const { isMerchant } = useContext(SettingsContext);
  const [showFeatureGatingModal, setShowFeatureGatingModal] = useState(false);

  const checkFeatureQuota = useCallback(async (feature, needed = 1, showModalImmediately = true) => {
    if (!isMerchant) {
      return true;
    }

    const quota = await loadFeatureQuota(feature);

    if (quota >= needed) {
      setShowFeatureGatingModal(false);

      return showModalImmediately ? true : { allowed: true };
    }

    if (user?.is_merchant_admin) {
      const featureType = amountFeatures.includes(feature) ? featureTypes.AMOUNT : featureTypes.BOOLEAN;

      if (showModalImmediately) {
        setShowFeatureGatingModal(featureType);
      } else {
        return { allowed: false, showModal: () => setShowFeatureGatingModal(featureType) };
      }
    }

    return false;
  }, [setShowFeatureGatingModal]);

  const values = useMemo(() => ({
    showFeatureGatingModal,
    setShowFeatureGatingModal,
    checkFeatureQuota,
  }), [showFeatureGatingModal, setShowFeatureGatingModal, checkFeatureQuota]);

  return (
    <FeatureGatingContext.Provider value={values}>
      {props.children}
    </FeatureGatingContext.Provider>
  )
}
